import { Fragment, useMemo } from 'react';

import {
  StyledBarChartContent,
  StyledSiteContainer,
  StyledWebsiteName,
  StyledValueContainer,
  StyledBar,
  StyledValueText,
  StyledGraphAxis,
} from './styles';

const truncateText = (text: string, maxLength: number) => {
  if (typeof text !== 'string') {
    return '';
  }
  if (text.length <= maxLength + 1) {
    return text;
  }
  return text.slice(0, maxLength).trim() + '…';
};

export type BarChartDataItem = {
  name: string;
  favicon?: string;
  value: number;
  isHighlighted?: boolean;
};

export type BarChartFormatter = (value: number) => string;

type BarChartProps = {
  chartData: BarChartDataItem[];
  chartWidth?: number;
  format: BarChartFormatter;
  nameMaxLength?: number;
};

export const BarChart = ({ chartData: chart, chartWidth = 160, format, nameMaxLength = 12 }: BarChartProps) => {
  const hasNegativeValue = useMemo(() => chart.some((d) => d.value < 0), [chart]);
  const hasOnlyNegativeValues = useMemo(() => chart.every((d) => d.value < 0), [chart]);

  const barWidth = (value: number) => {
    const biggestValue = chart[0]?.value ?? 0;
    const smallestValue = chart[chart.length - 1]?.value ?? 0;

    if (!hasNegativeValue) {
      return (chartWidth / biggestValue) * value;
    }

    if (hasOnlyNegativeValues) {
      return (chartWidth / Math.abs(smallestValue)) * Math.abs(value);
    }

    return Math.round((chartWidth / (biggestValue - smallestValue)) * Math.abs(value));
  };

  const originAt = () => {
    if (!hasNegativeValue) {
      return 0;
    }
    return barWidth(chart[chart.length - 1]?.value ?? 0);
  };

  return (
    <StyledBarChartContent>
      {chart.map(({ name, value, favicon, isHighlighted = false }) => (
        <Fragment key={name}>
          <StyledSiteContainer hasIcon={!!favicon}>
            {favicon ? <img src={favicon} alt={name} /> : null}
            <StyledWebsiteName isHighlighted={isHighlighted}>{truncateText(name, nameMaxLength)}</StyledWebsiteName>
          </StyledSiteContainer>
          <StyledValueContainer>
            <StyledBar
              width={barWidth(value)}
              isHighlighted={isHighlighted}
              isNegative={value < 0}
              originAt={originAt()}
            />
            <StyledValueText isHighlighted={isHighlighted}>{format(value)}</StyledValueText>
          </StyledValueContainer>
        </Fragment>
      ))}
      {hasNegativeValue ? <StyledGraphAxis left={originAt()} /> : null}
    </StyledBarChartContent>
  );
};
